// ----------------------------------------------------------------------- externos
import { esHueco, rolDe } from "../../shared/rolPersona";
import { useEffect, useState } from "react";

// ----------------------------------------------------------------------- internos
import { permisosDeTodos } from "../../services/permisos";
import { guardarVistaPrevia } from "../../services/verComo";
import Icono from "../Icono";
import "./css/VerComo.css";

/* El selector y la franja de "estás viendo como otra persona".
 *
 * LA FRANJA NO SE PUEDE CERRAR, y es la decisión más importante de todo esto.
 * La vista previa no cambia la sesión: todo lo que se toque sigue
 * ejecutándose con la cuenta propia. Alguien que se olvide de que la tiene
 * puesta puede guardar algo creyendo que lo guarda "como" otro. Una franja
 * que se puede minimizar se minimiza el primer día.
 *
 * Por eso también se apaga el fondo de pantalla mientras dura (lo hace
 * `Layout`): además de que se pidió así, es la señal que se ve de reojo sin
 * leer nada.
 */

const VerComo = ({ viendo, onCambiar }) => {
  const [abierto, setAbierto] = useState(false);
  const [personas, setPersonas] = useState(null); // null = todavía no se pidió
  const [error, setError] = useState(null);
  const [busqueda, setBusqueda] = useState("");

  // La lista se pide al ABRIR y no al cargar el hub: es una herramienta que se
  // usa de vez en cuando, y traer el padrón entero de permisos en cada arranque
  // sería un viaje de red que casi nunca sirve.
  useEffect(() => {
    if (!abierto || personas !== null) return;
    permisosDeTodos()
      .then((d) => setPersonas(d?.personas || []))
      .catch((e) => setError(e.message || "No se pudo leer la lista"));
  }, [abierto, personas]);

  const elegir = (p) => {
    const persona = p
      ? { mail: p.mail, nombre: p.nombre || p.mail, tiene: p.tiene || [] }
      : null;
    guardarVistaPrevia(persona);
    onCambiar(persona);
    setAbierto(false);
    setBusqueda("");
  };

  if (viendo) {
    return (
      <div className="vercomo-franja" role="status">
        <Icono nombre="personas" tam={18} />
        <div className="vercomo-franja-txt">
          <b>Estás viendo el hub como {viendo.nombre}</b>
          <span>
            Esto muestra qué <b>dibuja</b> el sistema para esa persona: el menú,
            el contenido de cada pantalla y también los paneles de FlotaBot. No
            prueba a qué puede entrar escribiendo la dirección.
          </span>
          {/* EL LÍMITE CAMBIÓ, Y HAY QUE DECIR EL NUEVO.
              Antes acá decía que las pantallas de FlotaBot no seguían la vista
              previa. Ya la siguen. Lo que NO puede seguirla son los datos que
              la base filtra por quien está conectado —`mi_mail()` en las
              funciones de Supabase—, porque eso sería iniciar sesión como otro
              y esto explícitamente no hace eso.
              Callar el límite nuevo repetiría el error viejo: una vista previa
              que no dice hasta dónde llega manda a revisar el permiso, que es
              lo único que suele estar bien. */}
          <span className="vercomo-franja-ojo">
            Lo que sigue siendo <b>tuyo</b>: todo lo que toques se guarda con tu
            cuenta, y lo que la base filtra por usuario —tus tickets, tus
            mensajes— se sigue contando por vos. Los permisos sí son los de{" "}
            {viendo.nombre}.
          </span>
        </div>
        <button className="vercomo-salir" onClick={() => elegir(null)}>
          Volver a lo mío
        </button>
      </div>
    );
  }

  const lista = (personas || []).filter((p) => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return true;
    return (
      (p.nombre || "").toLowerCase().includes(q) ||
      p.mail.toLowerCase().includes(q)
    );
  });

  return (
    <>
      <button
        className="hub-boton-icono vercomo-abrir"
        title="Ver el hub como otra persona"
        onClick={() => setAbierto(true)}
      >
        <Icono nombre="personas" tam={18} titulo="Ver como" />
      </button>

      {abierto && (
        <div className="vercomo-velo" onClick={() => setAbierto(false)}>
          <div className="vercomo-caja" onClick={(e) => e.stopPropagation()}>
            <h3>Ver el hub como…</h3>
            <p className="vercomo-nota">
              Sirve para comprobar qué le desbloqueó un permiso a alguien, sin
              tener que pedirle una captura. <b>No cambia tu sesión</b>: sólo
              cambia lo que se dibuja.
            </p>
            <input
              className="vercomo-buscar"
              placeholder="Buscar por nombre o mail…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              autoFocus
            />
            {error && <p className="vercomo-error">{error}</p>}
            {personas === null && !error && (
              <p className="vercomo-nota">Buscando la gente…</p>
            )}
            <div className="vercomo-lista">
              {lista.map((p) => (
                <button
                  key={p.mail}
                  className="vercomo-persona"
                  onClick={() => elegir(p)}
                >
                  <span className="vercomo-nombre">{p.nombre || p.mail}</span>
                  {/* Cuántos permisos tiene, al lado del nombre: es lo que
                      hace obvio de antemano si esta persona va a ver un hub
                      lleno o uno vacío. */}
                  <span className={"vercomo-meta"
                                   + (esHueco(p) ? " sin-dato" : "")}>
                    {rolDe(p)} · {(p.tiene || []).length}{" "}
                    {(p.tiene || []).length === 1 ? "permiso" : "permisos"}
                  </span>
                </button>
              ))}
              {personas !== null && lista.length === 0 && (
                <p className="vercomo-nota">Nadie coincide con esa búsqueda.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VerComo;
